import {Component, effect, inject, signal} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {DatePipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {OverlayBadge} from 'primeng/overlaybadge';
import {ElciaIcon} from '../../../icon/icon';
import {ElciaButton} from '../../../buttons/button/button';
import {ElciaDialog} from '../../../dialog/dialog';
import {CurrentUserService} from '../../../../services/current-user.service';
import {LogLevel, LogService, LogSummary} from '../../../../../api';

@Component({
  selector: 'elcia-invoicing-alert',
  imports: [
    ElciaIcon,
    ElciaButton,
    ElciaDialog,
    TranslatePipe,
    DatePipe,
    OverlayBadge,
  ],
  templateUrl: './invoicing-alert.html',
  styleUrl: './invoicing-alert.scss',
})
export class InvoicingAlert {
  private readonly logService = inject(LogService);
  private readonly currentUserService = inject(CurrentUserService);

  protected readonly LogLevel = LogLevel;
  protected readonly hasFinanceAccess = this.currentUserService.hasFinanceAccess;
  protected logs = signal<LogSummary[]>([]);
  protected isDialogDisplayed = signal(false);
  private loaded = false;

  constructor() {
    // currentUser() peut être encore nul au démarrage : on charge dès que la permission est connue.
    effect(() => {
      if (this.hasFinanceAccess() && !this.loaded) {
        this.loaded = true;
        this.logService.getUnresolvedLogs().subscribe(response => this.logs.set(response.logs));
      }
    });
  }

  protected openDialog() {
    this.isDialogDisplayed.set(true);
  }

  protected closeDialog() {
    this.isDialogDisplayed.set(false);
  }

  protected async resolve(log: LogSummary) {
    await lastValueFrom(this.logService.resolveLog(log.id));
    this.removeFromList(log);
  }

  protected async delete(log: LogSummary) {
    await lastValueFrom(this.logService.deleteLog(log.id));
    this.removeFromList(log);
  }

  protected async resolveAll() {
    await Promise.all(this.logs().map(async log => await lastValueFrom(this.logService.resolveLog(log.id))));
    this.logs.set([]);
    this.closeDialog();
  }

  private removeFromList(log: LogSummary) {
    this.logs.update(logs => logs.filter(l => l.id !== log.id));
    if (this.logs().length === 0) {
      this.closeDialog();
    }
  }
}
